import type { ReactNode } from "react";
import { getCardDefinition, type Rotation, type Tool } from "@saboteur/contracts/cards";
import "./cards.css";
import { cardLabel, tunnelPaths } from "./card-art";

function ToolDrawing({ tool }: { tool: Tool }) {
  if (tool === "PICKAXE")
    return (
      <g>
        <path d="M29 105 65 38" stroke="#775239" strokeWidth="10" />
        <path
          d="M22 40 Q55 18 83 54 L63 45 Q43 34 22 40"
          fill="#92a5a6"
          stroke="#342b25"
          strokeWidth="3"
        />
        <path d="m35 93 5 3" stroke="#d7b581" strokeWidth="3" />
      </g>
    );
  if (tool === "LANTERN")
    return (
      <g stroke="#342b25" strokeWidth="4">
        <path d="M39 39 V29 Q50 16 61 29 V39" fill="none" />
        <path d="m31 44 7-10h24l7 10-5 54H36Z" fill="#566e70" />
        <path d="M39 49h22v39H39Z" fill="#efbf58" />
        <path d="M50 52v31M30 99h40M32 43h36" />
        <path d="m50 59-6 15 7 7 5-11Z" fill="#fff0b4" stroke="none" />
      </g>
    );
  return (
    <g stroke="#342b25" strokeWidth="4">
      <path d="m22 49 8 40h43l9-40Z" fill="#788c91" />
      <path d="m29 49 8-17 16 4 10-8 11 21" fill="#b2aaa0" />
      <path d="M36 57v23M52 57v23M68 57v23" />
      <circle cx="37" cy="99" r="8" fill="#342b25" />
      <circle cx="67" cy="99" r="8" fill="#342b25" />
      <path d="M21 111h62" stroke="#896e53" />
    </g>
  );
}

export function ToolIcon({ tool, broken = false }: { tool: Tool; broken?: boolean }) {
  return (
    <svg
      className={`tool-icon ${broken ? "is-broken" : "is-ready"}`}
      viewBox="0 0 100 140"
      role="img"
      aria-label={`${tool.toLowerCase()} ${broken ? "broken" : "ready"}`}
    >
      <ToolDrawing tool={tool} />
      {broken ? <path d="m29 41 40 56m0-56L29 97" stroke="#ed8078" strokeWidth="10" /> : null}
    </svg>
  );
}

function Nuggets() {
  return (
    <g fill="#efbf58" stroke="#886127" strokeWidth="3">
      <path d="m21 76 9-24 24 4 10 25-22 15Z" />
      <path d="m52 86 4-20 20-7 12 23-14 15Z" />
      <path d="m34 53 12-19 18 4 8 19-19 8Z" />
      <path d="m30 62 5-5 13 3M61 76l11-7" fill="none" stroke="#fff0b4" />
    </g>
  );
}

function Illustration({ faceId }: { faceId: string }) {
  if (faceId === "goal-gold") return <Nuggets />;
  if (faceId === "goal-rock")
    return (
      <path
        d="m20 80 13-28 29-10 21 28-8 26-33 6Z M33 52l17 23 33-5M50 75l-8 27"
        fill="#91958f"
        stroke="#414e50"
        strokeWidth="3"
      />
    );
  if (faceId === "start")
    return (
      <g stroke="#c99d62" strokeWidth="5">
        <path d="M37 39v62M63 39v62M37 45h26M37 59h26M37 73h26M37 87h26M37 101h26" />
      </g>
    );
  if (faceId === "goal-back")
    return (
      <g stroke="#c2a575" fill="none">
        <path d="M12 12h76v116H12Z M18 18h64v104H18Z" />
        <path d="m50 32 27 38-27 38-27-38Z" strokeWidth="2" />
        <path d="m29 80 15-31 9 18 9-9 12 22Z" fill="#c2a575" />
        <circle cx="50" cy="70" r="42" strokeDasharray="2 7" />
      </g>
    );
  if (faceId === "map")
    return (
      <g stroke="#775f41" strokeWidth="3">
        <path d="m16 40 23-8 23 9 22-9v67l-22 9-23-9-23 8Z" fill="#f4e7c7" />
        <path d="M39 32v67m23-58v67" />
        <path d="m26 83 23-26 24 17" fill="none" strokeDasharray="5 4" />
        <path d="m67 68 12 12m0-12L67 80" stroke="#b76e48" />
      </g>
    );
  if (faceId === "rockfall")
    return (
      <g stroke="#514b43" strokeWidth="3" fill="#94978e">
        <path d="m20 87 14-21 22 10-4 29H26Z M60 58l21 10-2 32-23-10Z M36 30l20 3 10 17-22 9-14-12Z" />
        <path d="m20 32-6 20m63-33 9 23M21 113h63" fill="none" stroke="#b47751" />
      </g>
    );
  const definition = getCardDefinition(faceId);
  if (!("tools" in definition)) return null;
  return (
    <>
      <g transform={definition.tools.length > 1 ? "translate(-5 8) scale(.72)" : undefined}>
        <ToolDrawing tool={definition.tools[0]} />
      </g>
      {definition.tools.length > 1 ? (
        <g transform="translate(38 31) scale(.62)">
          <ToolDrawing tool={definition.tools[1]!} />
        </g>
      ) : null}
      <circle
        cx="77"
        cy="112"
        r="15"
        fill={definition.kind === "BREAK_TOOL" ? "#ed8078" : "#63c7b2"}
      />
      {definition.kind === "BREAK_TOOL" ? (
        <path d="m70 105 14 14m0-14-14 14" stroke="#342b25" strokeWidth="4" />
      ) : (
        <path d="m68 111 6 7 13-14" fill="none" stroke="#203832" strokeWidth="4" />
      )}
    </>
  );
}

function FaceFrame({
  label,
  children,
  dark = false,
}: {
  label: string;
  children: ReactNode;
  dark?: boolean;
}) {
  return (
    <svg className="card-face" viewBox="0 0 100 140" role="img" aria-label={label}>
      <rect width="100" height="140" rx="5" fill={dark ? "#263a3e" : "#e2d2ae"} />
      <path
        d="m7 22 8-5m65 103 10-6M14 117l6 4M78 17l8 7"
        stroke={dark ? "#385054" : "#c6b58f"}
        strokeWidth="2"
      />
      {children}
    </svg>
  );
}

export function PathCard({ faceId, rotation = 0 }: { faceId: string; rotation?: Rotation }) {
  const paths = tunnelPaths(faceId);
  return (
    <FaceFrame label={`${cardLabel(faceId)}, ${rotation} degrees`}>
      <g className="card-path-art" style={{ transform: `rotate(${rotation}deg)` }}>
        {[29, 23, 17].map((width, index) => (
          <g
            key={width}
            fill="none"
            stroke={["#b29a72", "#72604c", "#342b25"][index]}
            strokeWidth={width}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            {paths.map((d, i) => (
              <path key={i} d={d} data-network={index === 2 ? i : undefined} />
            ))}
          </g>
        ))}
        {faceId === "start" || faceId.startsWith("goal-") ? <Illustration faceId={faceId} /> : null}
      </g>
    </FaceFrame>
  );
}
export function ActionCard({ faceId }: { faceId: string }) {
  return (
    <FaceFrame label={cardLabel(faceId)}>
      <rect x="7" y="7" width="86" height="126" rx="3" fill="none" stroke="#baa27b" />
      <Illustration faceId={faceId} />
    </FaceFrame>
  );
}
export function GoalCard({
  face,
  rotation = 0,
}: {
  face?: "GOLD" | "ROCK" | null;
  rotation?: Rotation;
}) {
  return face ? (
    <PathCard faceId={face === "GOLD" ? "goal-gold" : "goal-rock"} rotation={rotation} />
  ) : (
    <FaceFrame label="Hidden destination" dark>
      <Illustration faceId="goal-back" />
    </FaceFrame>
  );
}
export function RoleCard({ role }: { role: "MINER" | "SABOTEUR" }) {
  return (
    <FaceFrame
      label={
        role === "MINER"
          ? "Miner: connect the entrance to gold"
          : "Saboteur: prevent the crew reaching gold"
      }
      dark
    >
      <path d="M22 120v-17q0-30 28-30t28 30v17" fill={role === "MINER" ? "#637e75" : "#976955"} />
      <circle cx="50" cy="58" r="24" fill="#d8b28b" />
      <path d="M23 57q0-38 27-38t27 38Z" fill="#bba273" />
      <path d="m29 69 21 35 21-35-21 10Z" fill="#e2d2ae" />
      <circle cx="50" cy="39" r="9" fill={role === "MINER" ? "#efbf58" : "#ed8078"} />
      <path d="M35 61h8m14 0h8" stroke="#342b25" strokeWidth="4" />
    </FaceFrame>
  );
}
export function CardFace({ faceId, rotation = 0 }: { faceId: string; rotation?: Rotation }) {
  if (faceId === "goal-back") return <GoalCard />;
  return faceId.startsWith("path-") || faceId === "start" || faceId.startsWith("goal-") ? (
    <PathCard faceId={faceId} rotation={rotation} />
  ) : (
    <ActionCard faceId={faceId} />
  );
}

export function GoldCard({ value }: { value: number }) {
  return (
    <FaceFrame label={`${value} gold nuggets`}>
      <g fill="#efbf58" stroke="#886127" strokeWidth="3">
        {Array.from({ length: value }, (_, i) => (
          <g
            key={i}
            transform={`translate(${value === 1 ? 0 : i % 2 === 0 ? -13 : 13} ${value === 1 ? 0 : i === 2 ? 20 : -12}) scale(${value === 1 ? 1 : 0.8})`}
          >
            <path d="m30 82-5-27 25-17 24 21-7 30-23 8Z" />
            <path d="m25 55 24 14 25-10M49 69l-5 28" fill="none" />
            <path d="m34 53 13-8" stroke="#fff0b4" />
          </g>
        ))}
      </g>
    </FaceFrame>
  );
}
